/**
 * RealtimeProvider: one role-scoped SSE connection (GET /api/events) per
 * signed-in tab. Components subscribe by frame type with useRealtimeEvent.
 * SSE is an invalidation signal only; consumers still read via React Query.
 */

import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiClientError, resolveApiBaseUrl } from '@/lib/api'
import { clientAppKeys } from '@/hooks/cases/queryKeys'
import type { CaseStreamEvent, TeamStreamEvent } from '@/hooks/cases/useCaseStream'

// `reconnected` is emitted here, never sent by the server: a `connected` frame after the first one.
export type RealtimeEvent =
  | CaseStreamEvent
  | TeamStreamEvent
  | { type: 'connected'; role: string }
  | { type: 'reconnected' }

type Listener = (e: RealtimeEvent) => void
type Subscribe = (type: RealtimeEvent['type'], l: Listener) => () => void

// No-op default: outside the provider (login, unit tests) nothing connects or throws.
const RealtimeContext = createContext<Subscribe>(() => () => {})

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const listeners = useRef(new Map<string, Set<Listener>>())

  useEffect(() => {
    const emit = (event: RealtimeEvent) => listeners.current.get(event.type)?.forEach((l) => l(event))
    const clientAppDetail = clientAppKeys.details()
    let es: EventSource | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    let delay = 5_000
    let stopped = false
    let seenConnected = false

    const connect = () => {
      es = new EventSource(`${resolveApiBaseUrl()}/api/events`, { withCredentials: true })
      es.onmessage = (m: MessageEvent) => {
        let event: RealtimeEvent
        try {
          event = JSON.parse(m.data) as RealtimeEvent
        } catch {
          return // ignore malformed frames
        }
        const reconnected = event.type === 'connected' && seenConnected
        if (event.type === 'connected') {
          seenConnected = true
          delay = 5_000
        }
        // Events may have been missed. Not the Client Info detail: a silent refetch would advance its
        // expectedUpdatedAt and hide a conflict; that page shows its stale banner on `reconnected` instead.
        if (reconnected) {
          void queryClient.invalidateQueries({
            predicate: (q) => !clientAppDetail.every((k, i) => q.queryKey[i] === k),
          })
        }
        emit(event)
        if (reconnected) emit({ type: 'reconnected' })
      }
      // CONNECTING: the browser retries by itself. CLOSED: it gave up (a non-200, e.g. Cloudflare's 5xx while
      // the api container is recreated on deploy), so retry here unless the session itself is gone.
      es.onerror = () => {
        if (es?.readyState !== EventSource.CLOSED) return
        api.get('/auth/session').then(
          () => true,
          // api.ts already ran the session-expired flow on a 401; any other failure is the api being down.
          (e: unknown) => !(e instanceof ApiClientError && e.statusCode === 401),
        ).then((retry) => {
          if (!retry || stopped) return
          timer = setTimeout(connect, delay)
          delay = Math.min(delay * 2, 60_000)
        })
      }
    }
    connect()

    return () => {
      stopped = true
      clearTimeout(timer)
      es?.close()
    }
  }, [queryClient])

  const subscribe = useMemo<Subscribe>(
    () => (type, l) => {
      const set = listeners.current.get(type) ?? new Set<Listener>()
      set.add(l)
      listeners.current.set(type, set)
      return () => void set.delete(l)
    },
    [],
  )

  return <RealtimeContext.Provider value={subscribe}>{children}</RealtimeContext.Provider>
}

export function useRealtimeEvent<T extends RealtimeEvent['type']>(
  type: T,
  onEvent: (e: Extract<RealtimeEvent, { type: T }>) => void,
): void {
  const subscribe = useContext(RealtimeContext)
  // Latest callback in a ref: identity changes don't resubscribe.
  const ref = useRef(onEvent)
  ref.current = onEvent
  useEffect(() => subscribe(type, (e) => ref.current(e as Extract<RealtimeEvent, { type: T }>)), [subscribe, type])
}
